import { useEffect, useRef, useState } from "react";
import { getSocket } from "../../socket/index";
import {
  getAllUnallocatedCases,
  applyAllocationCasesSync,
  fetchMissingFilledOrderCases,
  makeMissingFilledOrderZip,
  type AllocationCasesSyncPayload,
} from "../../store/allocation/readyCasesSlice";
import {
  assignCasesToEmployee,
  updateAllocatedCases,
} from "../../store/allocation/allocateSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { GroupedCase, CaseActionsMap, QC, PendingAction, SelectedRow } from "../../types/UnallocatedTypes";
import { Allocation, JobCardData } from "../../types/AllocationTypes";
import ActiveEmployees from "../../components/allocation-dashboard/activeEmployee";
import { useToast } from "../../components/utilities/Toast";
import { resetAllocationState } from "../../store/allocation/allocateSlice";
import { Helmet } from "react-helmet-async";
import CaseNotesModal from "../../components/allocation-dashboard/CaseNotesModal";
import CaseOkQueueModal from "../../components/allocation-dashboard/CaseOkQueueModal";
import ConfirmModal from "../../components/allocation-dashboard/ConfirmModal";
import JobCardModal from "../../components/allocation-dashboard/JobCardModal";
import EditAllocationModal from "./EditAllocationModal";
import { useNavigate } from "react-router-dom";
import { fetchAllEmployees } from "../../store/allocation/employeeSlice";
import { ModelPersonRow } from "../../components/admin/EmployeeNameEditor";
import {
  isAllocationQc2Complete,
} from "../../utils/allocationWorkflow";
import {
  ADMIN_ACCENT,
  AdminSearchInput,
  AdminSelect,
  adminTableHead,
} from "../../components/admin/AdminPageShell";
import {
  missingFilledOrderCase,
  unallocatedCases,
} from "../../types/ReadyCasesTypes";
import {
  RedesignScopeTag,
  splitNames,
} from "../../components/admin/unitsTableShared";
import {
  employeeNameListIncludes,
  isEmployeeInvolvedInCase,
  joinEmployeeNames,
  splitEmployeeNames,
} from "../../utils/employeeNames";
import {
  caseHrefFromAllocationId,
  isInternalRedesignCaseId,
} from "../../utils/caseIds";
import MyCasesToggle, {
  useMyCasesToggle,
} from "../../components/allocation-dashboard/MyCasesToggle";
import { isAdminLikeEmployee, isWorkflowEmployee } from "../../utils/employeeRoles";

const MAKE_ZIP_EMPLOYEE_ROLES = ["CRM", "Admin", "Executive", "Super Admin"];

const mapMissingZipToUnallocated = (
  item: missingFilledOrderCase,
): unallocatedCases => ({
  case_id: item.case_id,
  service_type: item.service_type || "filledOrder",
  delivery_units: item.uploaded_file_count ?? 0,
  creation_time: Number(item.creation_time_ms) || 0,
  patientNames: [],
  redesign_note: "",
  priority: "",
  details: item.case_owner
    ? `Missing zip — ${item.case_owner}`
    : "Missing zip",
  status: item.status || "",
  note_flag: false,
  allocation_id: 0,
  allocated_Designer: "",
  allocated_Captain: "",
  uploadTime: 0,
  rush: false,
  Designed: "",
  QC1: "",
  fileCheckSuccess: false,
  fileCheckErrors: item.uploaded_files
    ? [`No zip — files: ${item.uploaded_files}`]
    : ["No zip — client uploads present"],
  design_unit: null,
  job_card: null,
  tooth_numbers: "",
  QC2: "",
  note: item.uploaded_files || "",
  type: "missing_zip",
  comment_count: 0,
  redesign_category: "",
});

const getUnallocatedCaseKey = (row: { case_id: string; type: string }) =>
  `${row.case_id}:${row.type}`;

const playNewCaseSound = () => {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const playTone = (freq: number, start: number, duration: number) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(start);
      oscillator.stop(start + duration);
    };

    const now = ctx.currentTime;
    playTone(880, now, 0.15);
    playTone(1174.66, now + 0.16, 0.2);
    window.setTimeout(() => void ctx.close(), 500);
  } catch {
    // ignore audio failures (autoplay policy, unsupported browser, etc.)
  }
};

const AllocateLive = () => {
  type NoteKey = string;

  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const getRushKey = (caseId: string, patientName?: string) =>
    `${caseId}_${patientName || "case"}`;

  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { allEmployees } = useAppSelector(
    (state) => state.employee,
  );
  const { unallocatedCases, missingFilledOrderCases, loading, error } =
    useAppSelector((state) => state.readycases);
  const allocationState = useAppSelector((state) => state.caseAllocation);
  const [rushMap, setRushMap] = useState<Record<string, boolean>>({});
  const [zippingCaseId, setZippingCaseId] = useState<string | null>(null);

  const canMakeZip =
    user?.role === "Super Admin" ||
    (employee?.role != null &&
      MAKE_ZIP_EMPLOYEE_ROLES.includes(employee.role));

  const isSuperAdminPortal = user?.role === "Super Admin";
  const empRole = employee?.role || "";
  const isCrmViewOnly = empRole === "CRM" && !isSuperAdminPortal;
  const canWriteAllocation =
    isSuperAdminPortal ||
    ["Admin", "Executive", "Super Admin", "Captain", "Assistant Captain", "Senior Designer"].includes(empRole);
  const canHoldEdit =
    isSuperAdminPortal ||
    ["Admin", "Executive", "Super Admin", "Captain", "Assistant Captain"].includes(empRole);
  const canShowAllocateUi =
    isSuperAdminPortal ||
    ["Admin", "Executive", "Super Admin", "Captain", "Assistant Captain"].includes(empRole);

  const canOpenCaseOkQueue =
    isSuperAdminPortal ||
    user?.role === "Admin" ||
    user?.role === "Operations" ||
    empRole === "CRM" ||
    isAdminLikeEmployee(empRole) ||
    empRole === "Super Admin";

  const canQc1OnRow = (row: { type?: string; allocated_Captain?: string | null }) => {
    if (row.type !== "allocated") return false;
    if (isSuperAdminPortal || empRole === "Admin" || empRole === "Executive" || empRole === "Super Admin") {
      return true;
    }
    if (empRole === "Captain" || empRole === "Assistant Captain" || empRole === "Senior Designer") {
      return employeeNameListIncludes(row.allocated_Captain, employee?.emp_name);
    }
    return false;
  };

  const canDesignedOnRow = (row: { allocated_Designer?: string | null }) => {
    if (isCrmViewOnly) return false;
    if (canWriteAllocation) return true;
    if (empRole === "Designer") {
      return employeeNameListIncludes(row.allocated_Designer, employee?.emp_name);
    }
    return false;
  };

  const canQc2OnRow = () => canWriteAllocation && empRole !== "Designer";
  const showActionsColumn = isSuperAdminPortal || canMakeZip || canHoldEdit;

  const [open, setOpen] = useState<boolean>(false);
  const [showCaseOkQueue, setShowCaseOkQueue] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedAllocationId, setSelectedAllocationId] = useState<
    number | null
  >(null);
  const [selectedNotesCaseId, setSelectedNotesCaseId] = useState<string | null>(
    null,
  );

  const openCaseNotes = (
    allocationId?: number | null,
    caseId?: string | null,
  ) => {
    setSelectedAllocationId(allocationId ?? null);
    setSelectedNotesCaseId(caseId || null);
    setOpen(true);
  };

  const [notes, setNotes] = useState<Record<NoteKey, string>>({});
  const [selectedRows, setSelectedRows] = useState<SelectedRow[]>([]);
  const [allocateDesigner, setAllocateDesigner] = useState("");
  const [allocateCaptain, setAllocateCaptain] = useState("");
  const [allocatedIds, setAllocatedIds] = useState<string[]>([]);
  const [editNote, setEditNote] = useState("");


  // duplicate allocation prevention
  const [isAllocating, setIsAllocating] = useState(false);

  const [activeTextareaKey, setActiveTextareaKey] = useState<string | null>(
    null,
  );

  const [selectedCases, setSelectedCases] = useState<CaseActionsMap>({});
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passkey, setPasskey] = useState("");
  const [customQC, setCustomQC] = useState<QC | null>(null);
  const [actionNote, setActionNote] = useState("");
  const [currentUnix, setCurrentUnix] = useState(0);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [newDesigner, setNewDesigner] = useState("");
  const [newCaptain, setNewCaptain] = useState("");
  const [showJobCard, setShowJobCard] = useState(false);
  const [jobCardQueue, setJobCardQueue] = useState<
    { allocationId: number; mode: "designed" | "qc2" }[]
  >([]);
  const [jobCardDrafts, setJobCardDrafts] = useState<
    Record<number, JobCardData>
  >({});
  const [jobCardSubmitting, setJobCardSubmitting] = useState(false);
  const [jobCardIndex, setJobCardIndex] = useState(0);
  const [jobCardTotal, setJobCardTotal] = useState(0);

  const getNoteKey = (caseId: string, patientName?: string) =>
    `${caseId}_${patientName || "case"}`;

  const hasDesigned = (row: {
    Designed?: string | null;
    design_unit?: number | null;
  }) =>
    Boolean(row.Designed) ||
    (row.design_unit != null && Number(row.design_unit) > 0);

  const [caseGroupFilter, setCaseGroupFilter] = useState("all");
  // all | allocated | unallocated | reallocation

  const [caseSubFilter, setCaseSubFilter] = useState("all");
  // all | live | redesign

  const [blinkingCaseIds, setBlinkingCaseIds] = useState<Set<string>>(
    () => new Set(),
  );
  const initialLoadDoneRef = useRef(false);
  const knownCaseKeysRef = useRef<Set<string>>(new Set());

  const shouldNotifyNewCases = user?.role === "Super Admin" || employee?.role === "Captain" || employee?.role === "Assistant Captain" || isAdminLikeEmployee(employee?.role) || employee?.role === "Super Admin";
  const { showToggle, myCasesOnly, enabled: myCasesEnabled, setEnabled: setMyCasesEnabled } =
    useMyCasesToggle(employee);

  const isRedesign = (caseId: string = "") =>
    caseId.toLowerCase().startsWith("rd-");

  /* ---------- ENFORCEMENT FLAGS ---------- */
  const isDesignerSelected = Boolean(allocateDesigner);
  const isCaptainSelected = Boolean(allocateCaptain);
  const isCaseSelected = selectedRows.length > 0;
  const hasSelection = Object.keys(selectedCases).length > 0;

  const openJobCardForCase = (
    allocationId: number,
    mode: "designed" | "qc2",
  ) => {
    setJobCardDrafts({});
    setJobCardQueue([{ allocationId, mode }]);
    setJobCardIndex(0);
    setJobCardTotal(1);
    setShowJobCard(true);
  };

  const closeJobCard = (clearSelection = true) => {
    if (clearSelection) {
      const current = jobCardQueue[0];
      if (current) {
        setSelectedCases((prev) => {
          const action = current.mode === "qc2" ? "QC2" : "Designed";
          if (prev[current.allocationId] !== action) return prev;
          const { [current.allocationId]: _, ...rest } = prev;
          return rest;
        });
      }
    }
    setShowJobCard(false);
    setJobCardQueue([]);
    setJobCardDrafts({});
    setJobCardSubmitting(false);
    setJobCardIndex(0);
    setJobCardTotal(0);
  };

  const toggleAction = (allocationId: number, action: string) => {
    const row = unallocatedCases.find(
      (item) => Number(item.allocation_id) === Number(allocationId),
    );
    if (action === "QC1" && (!row || !canQc1OnRow(row))) return;
    if (action === "Designed" && (!row || !canDesignedOnRow(row))) return;
    if (action === "QC2" && !canQc2OnRow()) return;

    if (action === "Designed" || action === "QC2") {
      const isUnchecking = selectedCases[allocationId] === action;
      if (isUnchecking) {
        setSelectedCases((prev) => {
          const { [allocationId]: _, ...rest } = prev;
          return rest;
        });
        closeJobCard(false);
        return;
      }

      setSelectedCases((prev) => {
        const withoutJobCardActions = Object.fromEntries(
          Object.entries(prev).filter(
            ([, value]) => value !== "Designed" && value !== "QC2",
          ),
        );
        return {
          ...withoutJobCardActions,
          [allocationId]: action,
        };
      });
      openJobCardForCase(
        allocationId,
        action === "QC2" ? "qc2" : "designed",
      );
      return;
    }

    setSelectedCases((prev) => {
      if (prev[allocationId] === action) {
        const { [allocationId]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [allocationId]: action,
      };
    });
  };

  const cannotAllocate =
    employee?.role === "Designer" ||
    employee?.role === "Senior Designer" ||
    employee?.role === "CRM" ||
    employee?.role === "Operations";
  const isAllocateEnabled =
    !cannotAllocate &&
    isDesignerSelected &&
    isCaptainSelected &&
    isCaseSelected;

  useEffect(() => {
    if (loading || initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    knownCaseKeysRef.current = new Set(
      unallocatedCases.map(getUnallocatedCaseKey),
    );
  }, [loading, unallocatedCases]);

  useEffect(() => {
    const socket = getSocket();

    socket.emit("join_allocation_room");

    const handleCasesSync = (payload: AllocationCasesSyncPayload) => {
      if (shouldNotifyNewCases && initialLoadDoneRef.current) {
        const newArrivals = payload.upsert.filter(
          (row) =>
            (row.type === "live" ||
              row.type === "redesign" ||
              row.type === "internal_redesign") &&
            !knownCaseKeysRef.current.has(getUnallocatedCaseKey(row)),
        );

        if (newArrivals.length > 0) {
          playNewCaseSound();

          for (const row of newArrivals) {
            showToast(`New case arrival: ${row.case_id}`, "info", 2000);
            setBlinkingCaseIds((prev) => new Set(prev).add(row.case_id));
            window.setTimeout(() => {
              setBlinkingCaseIds((prev) => {
                const next = new Set(prev);
                next.delete(row.case_id);
                return next;
              });
            }, 2000);
          }
        }
      }

      for (const row of payload.upsert) {
        knownCaseKeysRef.current.add(getUnallocatedCaseKey(row));
      }

      dispatch(applyAllocationCasesSync(payload));
    };

    socket.on("allocation_cases_sync", handleCasesSync);

    return () => {
      socket.off("allocation_cases_sync", handleCasesSync);
    };
  }, [dispatch, shouldNotifyNewCases, showToast]);

  useEffect(() => {
    if (!allEmployees) {
      dispatch(fetchAllEmployees());
    }
  }, [dispatch, allEmployees]);


  function getCurrentISTUnix() {
    const now = new Date();
    const istTime = new Date(now.getTime());
    return Math.floor(istTime.getTime() / 1000);
  }

  useEffect(() => {
    const curr = getCurrentISTUnix();
    setCurrentUnix(curr);
    dispatch(getAllUnallocatedCases());
    dispatch(fetchMissingFilledOrderCases());
  }, [dispatch]);

  const QC1_SELECT_ROLES = ["Admin", "Executive", "Super Admin", "Captain", "Assistant Captain", "Senior Designer"];
  const QC2_SELECT_ROLES = ["CRM", "Senior Designer", "Captain", "Assistant Captain", "Super Admin"];
  const DESIGNED_SELECT_ROLES = [...QC2_SELECT_ROLES, "Designer"];

  const employeesForAction = (action: "designed" | "qc1" | "qc2") =>
    allEmployees?.filter(
      (item: any) =>
        item?.verified &&
        (action === "qc1"
          ? QC1_SELECT_ROLES
          : action === "qc2"
            ? QC2_SELECT_ROLES
            : DESIGNED_SELECT_ROLES
        ).includes(item.role),
    );
  const jobCardEmpList = employeesForAction(
    jobCardQueue[0]?.mode || "designed",
  );

  const handleMakeZip = async (caseId: string) => {
    if (!canMakeZip) {
      showToast(
        "Only Super Admin, CRM, Admin, or Super Admin employees can make zip",
        "error",
      );
      return;
    }

    setZippingCaseId(caseId);
    try {
      const result = await dispatch(
        makeMissingFilledOrderZip({
          caseId,
          userId: user?.user_id,
        }),
      ).unwrap();
      showToast(
        `Created ${result.zipFileName || "zip"} for ${caseId}`,
        "success",
      );
      dispatch(getAllUnallocatedCases());
    } catch (err) {
      showToast(
        typeof err === "string" ? err : "Failed to make zip for this case",
        "error",
      );
    } finally {
      setZippingCaseId(null);
    }
  };

  const missingZipRows = (missingFilledOrderCases || []).map(
    mapMissingZipToUnallocated,
  );

  const filteredCases = [...(unallocatedCases || []), ...missingZipRows].filter(
    (item: any) => {
    if (
      myCasesOnly &&
      !isEmployeeInvolvedInCase(item, employee?.emp_name)
    ) {
      return false;
    }

    const redesign = isRedesign(item.case_id);
    const internalRedesign =
      item.type === "internal_redesign" || isInternalRedesignCaseId(item.case_id);

    // 🧠 GROUP FILTER
    if (caseGroupFilter === "allocated" && item.type !== "allocated") return false;

    if (caseGroupFilter === "unallocated") {
      if (
        !(
          item.type === "live" ||
          item.type === "redesign" ||
          item.type === "internal_redesign" ||
          item.type === "missing_zip"
        )
      )
        return false;
    }

    if (caseGroupFilter === "reallocation" && item.type !== "reallocation") return false;

    // 🧠 SUB FILTER
    if (caseSubFilter === "live" && (redesign || internalRedesign)) return false;
    if (caseSubFilter === "redesign" && !redesign && !internalRedesign) return false;

    // 🔎 SEARCH
    const q = search.toLowerCase();
    if (
      !item.case_id?.toLowerCase().includes(q) &&
      !item.allocated_Designer?.toLowerCase().includes(q) &&
      !item.allocated_Captain?.toLowerCase().includes(q) &&
      !item.details?.toLowerCase().includes(q)
    ) {
      return false;
    }

    return true;
  });


  /* ---------- GROUP CASES ---------- */
  const groupedCases: GroupedCase[] = Object.values(
    filteredCases.reduce<Record<string, GroupedCase>>((acc, row) => {
      const groupKey =
        row.type === "allocated"
          ? `${row.case_id}_allocated_${row.allocation_id}`
          : `${row.case_id}_${row.type}`;

      const patients =
        row.service_type === "filledOrder" &&
          Array.isArray(row.patientNames) &&
          row.patientNames.length
          ? row.patientNames
          : [""];

      if (!acc[groupKey]) {
        acc[groupKey] = { ...row, patientList: [...patients] };
      } else {
        acc[groupKey].patientList.push(...patients);
      }

      return acc;
    }, {}),
  );

  const typePriority: Record<string, number> = {
    missing_zip: 0,
    live: 1,
    redesign: 2,
    internal_redesign: 2,
    reallocation: 3,
    unhold: 4,
    allocated: 5,
    hold: 6,
  };

  const sortedCases = [...groupedCases].sort((a, b) => {
    const priorityA = typePriority[a.type] ?? 999;
    const priorityB = typePriority[b.type] ?? 999;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return Number(b.creation_time) - Number(a.creation_time);
  });

  /* ---------- CASE SELECTION ---------- */
  const toggleRowSelection = (
    caseId: string,
    serviceType: string,
    caseType: string,
    patientName?: string,
    allocationId?: number,
  ) => {
    setSelectedRows((prev) => {
      const exists = prev.some(
        (r) =>
          r.case_id === caseId &&
          (serviceType !== "filledOrder" || r.patientName === patientName),
      );

      return exists
        ? prev.filter(
          (r) =>
            !(
              r.case_id === caseId &&
              (serviceType !== "filledOrder" || r.patientName === patientName)
            ),
        )
        : [
          ...prev,
          serviceType === "filledOrder"
            ? {
              case_id: caseId,
              patientName,
              caseType,
              allocation_id:
                caseType === "reallocation" ||
                caseType === "internal_redesign"
                  ? allocationId
                  : undefined,
            }
            : {
              case_id: caseId,
              caseType,
              allocation_id:
                caseType === "reallocation" ||
                caseType === "internal_redesign"
                  ? allocationId
                  : undefined,
            },
        ];
    });
  };

  /* ---------- EMPLOYEE SELECTION ---------- */
  const handleEmployeeSelect = (
    role: "Designer" | "Senior Designer" | "Captain",
    empName: string,
  ) => {
    if (role === "Designer") {
      // Toggle designers — multiple designers, one case; QC2 stays single later
      const current = splitEmployeeNames(allocateDesigner);
      const next = current.includes(empName)
        ? current.filter((name) => name !== empName)
        : [...current, empName];
      setAllocateDesigner(joinEmployeeNames(next));
    } else {
      setAllocateCaptain(empName === allocateCaptain ? "" : empName);
    }
  };

  /* ---------- ALLOCATION ---------- */
  const handleAllocate = () => {
    if (isAllocating) return; // 🔒 block multiple clicks
    if (cannotAllocate) {
      showToast("You do not have access to allocate cases", "error");
      return;
    }

    if (!allocateDesigner || !allocateCaptain) {
      showToast("Please select at least 1 Designer and 1 Captain", "error");
      return;
    }

    if (selectedRows.length === 0) {
      showToast("Please select at least one case", "error");
      return;
    }
    setIsAllocating(true);
    setAllocatedIds(selectedRows.map((r) => r.case_id));
    const allocation: Allocation = {
      cases: selectedRows.map((r) => {
        const key = getNoteKey(r.case_id, r.patientName);
        const rushKey = getRushKey(r.case_id, r.patientName);

        return {
          caseId: r.case_id,
          patientName: r.patientName ? [r.patientName] : [],
          allocatedUnits: 0,
          note: notes[key]?.trim() || "No note provided",
          type: r.caseType,
          rush: rushMap[rushKey] || false,
          allocationId: r.allocation_id,
        };
      }),

      allocatedBy:
        user?.role === "Super Admin"
          ? user?.user_name || ""
          : employee?.emp_name || "",
      allocatedDesigner: allocateDesigner,
      allocatedCaptain: allocateCaptain,
    };

    dispatch(assignCasesToEmployee({ assignedCases: [allocation] }))
      .unwrap()
      .catch(() => {
        // Toast is shown from allocationState.error
      })
      .finally(() => {
        setIsAllocating(false); // release lock
      });
  };

  /* ---------- FEEDBACK ---------- */
  useEffect(() => {
    if (!allocationState.success && !allocationState.error) return;

    if (allocationState.success) {
      // cleanup
      setAllocateDesigner("");
      setAllocateCaptain("");
      setSelectedRows([]);
      setNotes({});

      showToast("Allocation Successful", "success");
      dispatch(resetAllocationState());
    }
    if (allocationState.error) {
      const alreadyAllocated = /already allocated/i.test(
        allocationState.error,
      );
      showToast(
        alreadyAllocated ? "case already allocated" : allocationState.error,
        "error",
      );
      dispatch(getAllUnallocatedCases());
      dispatch(resetAllocationState());
    }
  }, [
    allocationState.success,
    allocationState.error,
    allocatedIds,
    dispatch,
    showToast,
  ]);

  /* ---------------- OPEN MODALS ---------------- */

  const handleUpdateClick = () => {
    const bulkActions = Object.entries(selectedCases).filter(
      ([, action]) => action !== "Designed" && action !== "QC2",
    );
    if (bulkActions.length === 0) return;

    setPendingAction({ type: "bulk" });
    setShowConfirm(true);
  };

  const handleJobCardSubmit = (
    jobCard: JobCardData,
    options?: { client_comments_read?: boolean },
  ) => {
    const current = jobCardQueue[0];
    if (!current) return;
    const jobCardRow = unallocatedCases.find(
      (item) => Number(item.allocation_id) === Number(current.allocationId),
    );
    if (current.mode === "qc2" && !canQc2OnRow()) return;
    if (current.mode === "designed" && (!jobCardRow || !canDesignedOnRow(jobCardRow))) {
      return;
    }

    const isAdmin = user?.role === "Super Admin";
    const finalPasskey = isAdmin ? user?.user_name : passkey;
    const updateCases = [
      {
        allocationId: current.allocationId,
        action: current.mode === "qc2" ? "QC2" : "Designed",
        action_note: current.mode === "qc2" ? actionNote : undefined,
        job_card: jobCard,
        ...(options?.client_comments_read
          ? { client_comments_read: true }
          : {}),
      },
    ];

    const payload = finalPasskey
      ? {
          passkey: isAdmin && customQC?.name ? customQC.name : finalPasskey,
          updateCases,
          isAdmin,
        }
      : {
          empId: customQC && customQC.id > 0 ? customQC.id : employee?.emp_id,
          updateCases,
          isAdmin,
        };

    if (!payload.passkey && !payload.empId) {
      showToast("Unable to save job card without an operator identity", "error");
      return;
    }

    setJobCardSubmitting(true);
    dispatch(updateAllocatedCases(payload as any))
      .unwrap()
      .then(() => {
        closeJobCard();
        setPendingAction(null);
        setPasskey("");
        setActionNote("");
        showToast(
          current.mode === "qc2"
            ? "QC2 updated with job card"
            : "Designed check saved",
          "success",
        );
      })
      .catch((err) => {
        showToast(err || "Failed to save job card", "error");
      })
      .finally(() => {
        setJobCardSubmitting(false);
      });
  };

  const handleSingleActionClick = (
    allocationId: number,
    action: "Transfer" | "Hold" | "Unhold" | "Close",
  ) => {
    setPendingAction({ type: "single", allocationId, action });
    setShowConfirm(true);
  };

  /* ---------------- CONFIRM HANDLER ---------------- */

  const handleConfirm = () => {
    if (!pendingAction) return;

    const isAdmin = user?.role === "Super Admin";
    const finalPasskey = isAdmin ? user?.user_name : passkey;

    let payload: any;

    if (pendingAction.type === "bulk") {
      payload = finalPasskey
        ? {
          passkey: isAdmin && customQC?.name ? customQC.name : finalPasskey,
          updateCases: Object.entries(selectedCases)
            .filter(([, action]) => action !== "Designed" && action !== "QC2")
            .map(([allocationId, action]) => ({
              allocationId: Number(allocationId),
              action,
              action_note: action === "QC2" ? actionNote : undefined,
            })),
          isAdmin,
        }
        : {
          empId: customQC && customQC.id > 0 ? customQC.id : employee?.emp_id,
          updateCases: Object.entries(selectedCases)
            .filter(([, action]) => action !== "Designed" && action !== "QC2")
            .map(([allocationId, action]) => ({
              allocationId: Number(allocationId),
              action,
              action_note: action === "QC2" ? actionNote : undefined,
            })),
          isAdmin,
        };
    }

    if (pendingAction.type === "single") {
      payload = employee
        ? {
          empId: customQC && customQC.id > 0 ? customQC.id : employee.emp_id,
          updateCases: [
            {
              allocationId: pendingAction.allocationId,
              action: pendingAction.action,
              action_note: actionNote,
            },
          ],
          isAdmin,
        }
        : {
          passkey: isAdmin && customQC?.name ? customQC.name : finalPasskey,
          updateCases: [
            {
              allocationId: pendingAction.allocationId,
              action: pendingAction.action,
              action_note: actionNote,
            },
          ],
          isAdmin,
        };
    }

    dispatch(updateAllocatedCases(payload))
      .unwrap()
      .then(() => {
        // dispatch(getAllUnallocatedCases()); // 🔥 UI updates instantly
        setShowConfirm(false);
        setPendingAction(null);
        setPasskey("");
        setActionNote("");
        setSelectedCases({});
      })
      .catch(() => {
        // ❌ do nothing here – error is already in redux state
      });
  };

  const hasBulkQC2 =
    pendingAction?.type === "bulk" &&
    Object.values(selectedCases).includes("QC2");
  const hasBulkQC1 =
    pendingAction?.type === "bulk" &&
    Object.values(selectedCases).includes("QC1");
  const confirmSelectLabel = hasBulkQC2
    ? "QC2"
    : hasBulkQC1
      ? "QC1"
      : "employee";
  const filteredEmployees = employeesForAction(hasBulkQC1 ? "qc1" : "qc2");

  useEffect(() => {
    const handleClickOutside = () => {
      setActiveTextareaKey(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleEditClick = (row: any) => {
    setEditData(row);
    setNewDesigner(row.allocated_Designer);
    setNewCaptain(row.allocated_Captain);
    setEditNote(""); // default empty OR row.note if available
    setShowEditModal(true);
  };

  const handleEditSave = () => {
    if (!editData) return;

    const isAdmin = user?.role === "Super Admin";
    const isEmployee = Boolean(employee);

    let payload: any = {
      updateCases: [
        {
          allocationId: editData.allocation_id,
          newDesigner,
          newCaptain,
          action: "EDIT",
          action_note: editNote?.trim()
            ? editNote
            : `No note provided for edit - ${newDesigner} / ${newCaptain}`,
        },
      ],
      isAdmin,
    };

    if (isAdmin) {
      payload.passkey = user?.user_name;
    } else if (isEmployee) {
      payload.empId = employee?.emp_id;
    } else {
      payload.passkey = passkey;
    }

    dispatch(updateAllocatedCases(payload))
      .unwrap()
      .then(() => {
        setShowEditModal(false);
        setEditData(null);
        setPasskey("");
      })
      .catch(() => {
        showToast("Update Failed", "error");
      });
  };

  const [expandedCells, setExpandedCells] = useState<Record<string, boolean>>({});
  const getCellKey = (row: any, patientIndex: number) =>
    `${row.case_id}-${patientIndex}`;
  const getErrorKey = (row: any) => `error-${row.case_id}`;

  const currentJobCard = jobCardQueue[0];
  const currentJobCardRow =
    unallocatedCases.find(
      (row) =>
        Number(row.allocation_id) === Number(currentJobCard?.allocationId),
    ) ?? null;

  return (
    <div className="h-full w-full p-2">
      <Helmet>
        <title>Allocation Portal</title>
      </Helmet>
      <div className="w-full gap-3 flex flex-col">
        {/* Designers */}

        {(canShowAllocateUi) && (
            <ActiveEmployees
              selectedDesigner={allocateDesigner}
              selectedCaptain={allocateCaptain}
              onSelect={handleEmployeeSelect}
            />
          )}

        <div
          className="rounded-2xl px-3 py-2.5 md:px-4 flex flex-wrap items-center justify-between gap-2.5 shadow-sm"
          style={{ backgroundColor: ADMIN_ACCENT }}
        >
          <div className="flex flex-wrap items-center gap-2">
            {canShowAllocateUi ? (
              <button
                disabled={!isAllocateEnabled || isAllocating}
                className={`px-3.5 py-2 rounded-xl text-xs font-medium text-white transition
    ${isAllocateEnabled && !isAllocating
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-white/25 cursor-not-allowed text-white/70"
                  }
  `}
                onClick={handleAllocate}
              >
                {isAllocating ? "Allocating..." : "Allocate"}
              </button>
            ) : null}

            <button
              onClick={() => navigate("/dashboard/allocation/updates")}
              className="px-3 py-2 rounded-xl text-xs font-medium border border-white/40 bg-white text-[#4A4A48] hover:bg-white/90 transition"
            >
              12 hr cases
            </button>
            <button
              onClick={() => navigate("/dashboard/allocation/logs")}
              className="px-3 py-2 rounded-xl text-xs font-medium border border-white/40 bg-white text-[#4A4A48] hover:bg-white/90 transition"
            >
              24 hr cases
            </button>
            <button
              onClick={() =>
                window.open(
                  "/dashboard/allocation/history",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="px-3 py-2 rounded-xl text-xs font-medium border border-white/40 bg-white text-[#4A4A48] hover:bg-white/90 transition"
            >
              History
            </button>
            {canOpenCaseOkQueue ? (
              <button
                onClick={() => setShowCaseOkQueue(true)}
                className="px-3 py-2 rounded-xl text-xs font-medium border border-white/40 bg-white text-[#4A4A48] hover:bg-white/90 transition"
              >
                Case OK Queue
              </button>
            ) : null}

            <MyCasesToggle
              show={showToggle}
              enabled={myCasesEnabled}
              empName={employee?.emp_name}
              onChange={setMyCasesEnabled}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 flex-1 justify-center min-w-[200px]">
            <AdminSearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by Case ID, Designer or Captain"
              className="w-full sm:w-[260px]"
              size="sm"
            />

            <AdminSelect
              value={caseGroupFilter}
              onChange={setCaseGroupFilter}
              size="sm"
            >
              <option value="all">All</option>
              <option value="allocated">Allocated</option>
              <option value="unallocated">Unallocated</option>
              <option value="reallocation">Reallocation</option>
            </AdminSelect>

            <AdminSelect
              value={caseSubFilter}
              onChange={setCaseSubFilter}
              size="sm"
            >
              <option value="all">All</option>
              <option value="live">Live</option>
              <option value="redesign">Redesign</option>
            </AdminSelect>
          </div>

          <div className="flex gap-3 items-center">
            <span className="text-xs font-medium text-white/90">
              Total: {unallocatedCases?.length || 0}
            </span>
            {canWriteAllocation ? (
            <button
              onClick={handleUpdateClick}
              disabled={!hasSelection}
              className={`px-3.5 py-2 text-xs font-medium text-white rounded-xl transition ${!hasSelection
                ? "bg-white/25 cursor-not-allowed text-white/70"
                : "bg-green-500 hover:bg-green-600"
                }`}
            >
              Update
            </button>
            ) : null}
          </div>
        </div>
        {!loading && !error && (
          <div className="rounded-2xl border border-[#E8E8E4] bg-white shadow-sm overflow-hidden">
            <div className="h-[calc(100vh-260px)] overflow-y-auto overflow-x-auto">
              <table className="min-w-full table-fixed border-collapse">
                <thead className="bg-[#FDFBF2] sticky top-0 z-10">
                  <tr className="border-b border-[#E8E8E4]">
                    <th className={adminTableHead}>S.No</th>
                    <th className={adminTableHead}>Case ID</th>
                    <th className={adminTableHead}>Details</th>
                    <th className={adminTableHead}>Units / Patient</th>
                    <th className={adminTableHead}>DateTime</th>
                    <th className={adminTableHead}>Rush</th>
                    <th className={adminTableHead}>Note</th>
                    <th className={adminTableHead}>Select</th>
                    <th className={adminTableHead}>Captain</th>
                    <th className={adminTableHead}>Designer</th>
                    <th className={adminTableHead}>QC1</th>
                    <th className={adminTableHead}>Checker</th>
                    <th className={adminTableHead}>Designed</th>
                    <th className={adminTableHead}>QC2</th>
                    {showActionsColumn && (
                        <th className={`${adminTableHead} text-center`}>Actions</th>
                      )}
                  </tr>
                </thead>

                <tbody>
                  {sortedCases.map((row, caseIndex) =>
                    row.patientList.map((patient, patientIndex) => {
                      const isSelected = selectedRows.some(
                        (r) =>
                          r.case_id === row.case_id &&
                          (row.service_type !== "filledOrder" ||
                            r.patientName === patient),
                      );

                      const isNewCaseBlinking = blinkingCaseIds.has(row.case_id);
                      const rowBgClass = isNewCaseBlinking
                        ? "blink-new-case text-black"
                        : row.type === "missing_zip"
                          ? "bg-teal-200 hover:bg-teal-300 text-black"
                          : row.type === "live" ||
                              row.type === "redesign" ||
                              row.type === "internal_redesign"
                          ? "bg-white hover:bg-gray-100 text-black"
                          : row.type === "reallocation"
                            ? "bg-yellow-200 hover:bg-yellow-300"
                            : row.type === "hold"
                              ? "bg-gray-200 opacity-40"
                              : row.type === "allocated"
                                ? row?.rush
                                  ? "bg-purple-300 hover:bg-purple-400"
                                  : row?.uploadTime < currentUnix
                                    ? "bg-red-300 hover:bg-red-400 blink-expired"
                                    : row?.uploadTime - currentUnix <
                                      2 * 60 * 60
                                      ? "bg-red-400 hover:bg-red-500"
                                      : row?.uploadTime - currentUnix <
                                        4 * 60 * 60
                                        ? "bg-orange-200 hover:bg-orange-300"
                                        : row.case_id.length > 7
                                          ? "bg-[#d9d9d9] hover:bg-[#c0b8b8]"
                                          : "bg-[#b6d7a8] hover:bg-[#9abe8b]"
                                : "";

                      return (
                        <tr
                          key={`${row.case_id}-${patientIndex}`}
                          className={`border-t py-1 ${rowBgClass}`}
                        >
                          <td className="px-2 py-1  break-words whitespace-normal align-middle">
                            <div className="w-full h-full flex justify-center items-center">
                              {patientIndex === 0 ? caseIndex + 1 : ""}
                            </div>
                          </td>

                          <td className="px-2 py-1  break-words whitespace-normal">
                            <div className="relative w-full p-2 h-full flex flex-col justify-center items-center gap-1">
                              <a
                                href={caseHrefFromAllocationId(row.case_id)}
                                target="_blank"
                                className="block w-full h-full underline text-center"
                              >
                                {patientIndex === 0 ? row.case_id : ""}
                              </a>
                              {patientIndex === 0 ? (
                                <RedesignScopeTag labels={row.redesign_category} />
                              ) : null}
                              {row.comment_count > 0 && (
                                <div className="w-4 h-4 absolute bg-red-500 text-white top-0 right-0 rounded-full text-xs flex items-center justify-center">
                                  {row.comment_count}
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-2 py-1 max-w-[10vw] text-center align-middle">
                            <div className="w-full flex flex-col items-center">
                              {(() => {
                                const content =
                                  patientIndex === 0
                                    ? row.type === "reallocation" ||
                                      row.type === "internal_redesign" ||
                                      row.type === "allocated" ||
                                      row.type === "hold" ||
                                      row.type === "missing_zip"
                                      ? row.details
                                      : row.service_type
                                    : "";

                                const key = getCellKey(row, patientIndex);
                                const isExpanded = expandedCells[key];

                                return (
                                  <>
                                    <div
                                      className={`w-full break-all text-center ${isExpanded ? "" : "line-clamp-1"
                                        }`}
                                    >
                                      {content}
                                    </div>

                                    {content && content.length > 40 && (
                                      <button
                                        className="mt-1 text-gray-600 hover:text-black"
                                        onClick={() =>
                                          setExpandedCells((prev) => ({
                                            ...prev,
                                            [key]: !prev[key],
                                          }))
                                        }
                                      >
                                        {isExpanded ? "▲" : "▼"}
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>

                          <td className="px-2 py-2 max-w-[10vw] break-all whitespace-normal text-center align-middle">
                            <div className="w-full h-full flex justify-center items-center">
                              {patient || row.delivery_units || "-"}
                            </div>
                          </td>

                          <td className="px-2 py-1 break-words whitespace-normal">
                            <div className="w-full h-full flex justify-center items-center">
                              {new Date(
                                Number(row.creation_time),
                              ).toLocaleDateString("en-IN", {
                                timeZone: "Asia/Kolkata",
                                month: "short", // Feb
                                day: "2-digit", // 14
                              })}{" "}
                              {new Date(
                                Number(row.creation_time),
                              ).toLocaleTimeString("en-IN", {
                                timeZone: "Asia/Kolkata",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true, // change to false if you want 24-hour format
                              })}
                            </div>
                          </td>

                          <td className="px-2 py-1 font-medium break-words whitespace-normal">
                            <div className="w-full h-full flex justify-center items-center">
                              {row.type === "allocated" && row.rush ? (
                                <span>✅</span>
                              ) : (
                                <input
                                  type="checkbox"
                                  className="cursor-pointer"
                                  checked={
                                    row.priority?.toLowerCase() === "high"
                                      ? true
                                      : rushMap[
                                      getRushKey(row.case_id, patient)
                                      ] || false
                                  }
                                  disabled={
                                    row.priority?.toLowerCase() === "high" ||
                                    row.type === "allocated" ||
                                    row.type === "hold" ||
                                    row.type === "missing_zip" ||
                                    isWorkflowEmployee(employee?.role)
                                  }
                                  onChange={(e) => {
                                    const key = getRushKey(
                                      row.case_id,
                                      patient,
                                    );
                                    setRushMap((prev) => ({
                                      ...prev,
                                      [key]: e.target.checked,
                                    }));
                                  }}
                                />
                              )}
                            </div>
                          </td>

                          <td className="px-2 py-1 break-words whitespace-normal min-w-[5vw] max-w-[10vw] align-middle">
                            <div className="w-full h-full flex justify-center items-center">
                              {row.type === "allocated" ||
                                row.type === "hold" ? (
                                <div className="text-xs flex items-center justify-center gap-1">
                                  {row.note_flag ? <p>{row.note}</p> : null}
                                  <button
                                    className="px-2 text-xs py-1 bg-gray-500 text-white rounded-md"
                                    onClick={() =>
                                      openCaseNotes(
                                        row.allocation_id,
                                        row.case_id,
                                      )
                                    }
                                  >
                                    View
                                  </button>
                                </div>
                              ) : row.type === "missing_zip" ? (
                                <div
                                  className="text-xs text-center break-words line-clamp-2"
                                  title={row.note || undefined}
                                >
                                  {row.note || "N/A"}
                                </div>
                              ) : row.type === "reallocation" ? (
                                <div
                                  className="flex items-start gap-2"
                                  onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
                                >
                                  <textarea
                                    rows={1}
                                    className="border-2 border-gray-500 rounded-md px-2 py-1 text-xs w-full resize-none overflow-hidden"
                                    value={
                                      notes[getNoteKey(row.case_id, patient)] ||
                                      ""
                                    }
                                    onFocus={() =>
                                      setActiveTextareaKey(
                                        getNoteKey(row.case_id, patient),
                                      )
                                    }
                                    onChange={(e) => {
                                      const key = getNoteKey(
                                        row.case_id,
                                        patient,
                                      );
                                      setNotes((prev) => ({
                                        ...prev,
                                        [key]: e.target.value,
                                      }));
                                    }}
                                    onInput={(e) => {
                                      const el = e.currentTarget;
                                      el.style.height = "auto";
                                      el.style.height = el.scrollHeight + "px";
                                    }}
                                  />

                                  {activeTextareaKey !==
                                    getNoteKey(row.case_id, patient) && (
                                      <button
                                        className="px-2 py-1 bg-gray-500 text-white rounded-md"
                                        onClick={() =>
                                          openCaseNotes(
                                            row.allocation_id,
                                            row.case_id,
                                          )
                                        }
                                      >
                                        View
                                      </button>
                                    )}
                                </div>
                              ) : (
                                <div
                                  className="flex items-start gap-2 w-full"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <textarea
                                    rows={1}
                                    className="border-2 border-gray-500 rounded-md px-2 py-1 text-xs resize-none overflow-hidden w-full"
                                    value={
                                      notes[getNoteKey(row.case_id, patient)] ||
                                      ""
                                    }
                                    onChange={(e) => {
                                      const key = getNoteKey(
                                        row.case_id,
                                        patient,
                                      );
                                      setNotes((prev) => ({
                                        ...prev,
                                        [key]: e.target.value,
                                      }));
                                    }}
                                    onInput={(e) => {
                                      const el = e.currentTarget;
                                      el.style.height = "auto";
                                      el.style.height = el.scrollHeight + "px";
                                    }}
                                  />
                                  <button
                                    className="px-2 py-1 bg-gray-500 text-white rounded-md"
                                    onClick={() =>
                                      openCaseNotes(
                                        row.allocation_id,
                                        row.case_id,
                                      )
                                    }
                                  >
                                    View
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-2 py-1  break-words whitespace-normal">
                            <div className="w-full h-full flex justify-center items-center">
                              {(row.service_type !== "filledOrder" ||
                                patientIndex >= 0) && (
                                  <input
                                    type="checkbox"
                                    checked={
                                      isSelected || row.type === "allocated"
                                    }
                                    disabled={
                                      cannotAllocate ||
                                      row.type === "allocated" ||
                                      row.type === "hold" ||
                                      row.type === "missing_zip"
                                    }
                                    className={` ${
                                      cannotAllocate ||
                                      row.type === "allocated" ||
                                      row.type === "hold" ||
                                      row.type === "missing_zip"
                                        ? `cursor-not-allowed`
                                        : `cursor-pointer`
                                    }`}
                                    onChange={() =>
                                      toggleRowSelection(
                                        row.case_id,
                                        row.service_type,
                                        row.type,
                                        row.service_type === "filledOrder"
                                          ? patient
                                          : undefined,
                                        row.allocation_id,
                                      )
                                    }
                                  />
                                )}
                            </div>
                          </td>

                          <td className="px-2 py-1  break-words whitespace-normal">
                            <div className="w-full h-full flex justify-center items-center">
                              {patientIndex === 0 ? row.allocated_Captain : ""}
                            </div>
                          </td>
                          <td className="px-2 py-1  break-words whitespace-normal">
                            <div className="w-full h-full flex flex-col justify-center items-center gap-1">
                              {patientIndex === 0 ? (
                                <>
                                  <span>{row.allocated_Designer}</span>
                                  {(() => {
                                    if (!row.job_card?.model) return null;
                                    const modelBy = row.job_card.model_by || "";
                                    const modelUnits =
                                      row.job_card.model_units ?? 1;
                                    const designers = splitNames(
                                      row.allocated_Designer,
                                    );
                                    if (
                                      !modelBy ||
                                      (designers.includes(modelBy) &&
                                        modelUnits === 1)
                                    ) {
                                      return null;
                                    }
                                    return (
                                      <ModelPersonRow
                                        name={modelBy}
                                        options={[]}
                                        units={modelUnits}
                                      />
                                    );
                                  })()}
                                </>
                              ) : null}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="w-full h-full flex justify-center items-center">
                              {row.QC1 ? (
                                "✅"
                              ) : (
                                <input
                                  type="checkbox"
                                  disabled={!canQc1OnRow(row)}
                                  className={`${!canQc1OnRow(row) ? "cursor-not-allowed" : "cursor-pointer"}`}
                                  checked={
                                    selectedCases[row.allocation_id] === "QC1"
                                  }
                                  onChange={() =>
                                    toggleAction(row.allocation_id, "QC1")
                                  }
                                />
                              )}
                            </div>
                          </td>

                          <td className="px-2 py-1 text-center max-w-[10vw] align-middle">
                            {row.fileCheckSuccess ? (
                              "✅"
                            ) : row.fileCheckErrors?.length > 0 ? (
                              (() => {
                                const key = getErrorKey(row);
                                const isExpanded = expandedCells[key];

                                return (
                                  <div className="flex flex-col items-center">
                                    <div
                                      className={`text-red-500 text-sm w-full break-words ${isExpanded ? "" : "line-clamp-2"
                                        }`}
                                    >
                                      {row.fileCheckErrors.map((error: string, index: number) => (
                                        <div key={index}>• {error}</div>
                                      ))}
                                    </div>

                                    {(row.fileCheckErrors.length > 1 ||
                                      row.fileCheckErrors.join("").length > 60) && (
                                        <button
                                          className="mt-1 text-gray-600 hover:text-black transition"
                                          onClick={() =>
                                            setExpandedCells((prev) => ({
                                              ...prev,
                                              [key]: !prev[key],
                                            }))
                                          }
                                        >
                                          {isExpanded ? "▲" : "▼"}
                                        </button>
                                      )}
                                  </div>
                                );
                              })()
                            ) : (
                              "N/A"
                            )}
                          </td>

                          <td className="px-2 py-1 text-center align-middle">
                            <div className="w-full h-full flex justify-center items-center">
                              {row.Designed ? (
                                "✅"
                              ) : (
                                <input
                                  type="checkbox"
                                  disabled={
                                    !canDesignedOnRow(row) ||
                                    row.type !== "allocated" ||
                                    !row.QC1 ||
                                    isAllocationQc2Complete(row.status)
                                  }
                                  className={`${
                                    !canDesignedOnRow(row) ||
                                    row.type !== "allocated" ||
                                    !row.QC1 ||
                                    isAllocationQc2Complete(row.status)
                                      ? "cursor-not-allowed"
                                      : "cursor-pointer"
                                  }`}
                                  checked={
                                    selectedCases[row.allocation_id] ===
                                    "Designed"
                                  }
                                  onChange={() =>
                                    toggleAction(row.allocation_id, "Designed")
                                  }
                                />
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="w-full h-full flex flex-col justify-center items-center gap-0.5">
                              {isAllocationQc2Complete(row.status) ? (
                                row.QC2 || "✅"
                              ) : (
                                <>
                                  {row.QC2 ? (
                                    <span
                                      className="text-[10px] text-gray-500 leading-tight"
                                      title="Assigned — not completed until QC2 is checked"
                                    >
                                      {row.QC2}
                                    </span>
                                  ) : null}
                                  <input
                                    type="checkbox"
                                    disabled={
                                      !canQc2OnRow() ||
                                      !row?.QC1 ||
                                      !hasDesigned(row) ||
                                      row.type === "hold"
                                    }
                                    className={`${
                                      !canQc2OnRow() ||
                                      !row?.QC1 ||
                                      !hasDesigned(row)
                                        ? `cursor-not-allowed`
                                        : `cursor-pointer`
                                    }`}
                                    checked={
                                      selectedCases[row.allocation_id] === "QC2"
                                    }
                                    onChange={() =>
                                      toggleAction(row.allocation_id, "QC2")
                                    }
                                  />
                                </>
                              )}
                            </div>
                          </td>

                          {showActionsColumn && (
                            <td className="px-2 py-1">
                              <div className="flex justify-center gap-2">
                                {row.type === "missing_zip" ? (
                                  canMakeZip ? (
                                    <button
                                      type="button"
                                      disabled={Boolean(zippingCaseId)}
                                      onClick={() =>
                                        handleMakeZip(row.case_id)
                                      }
                                      className="px-3 py-1.5 text-xs bg-[#1a1a1a] text-white rounded disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      {zippingCaseId === row.case_id
                                        ? "Making zip…"
                                        : "Make Zip"}
                                    </button>
                                  ) : (
                                    <span className="text-xs text-gray-500">
                                      Missing zip
                                    </span>
                                  )
                                ) : canHoldEdit && row.type === "hold" ? (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() =>
                                        handleSingleActionClick(
                                          row.allocation_id,
                                          "Unhold",
                                        )
                                      }
                                      className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded"
                                    >
                                      Unhold
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleSingleActionClick(
                                          row.allocation_id,
                                          "Close",
                                        )
                                      }
                                      className="px-3 py-1.5 text-xs bg-red-500 text-white rounded"
                                    >
                                      Close
                                    </button>
                                  </div>
                                ) : canHoldEdit && row.type === "allocated" ? (
                                  <>
                                    <button
                                      onClick={() => handleEditClick(row)}
                                      className="px-2 py-1 text-xs bg-blue-500 text-white rounded"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleSingleActionClick(
                                          row.allocation_id,
                                          "Hold",
                                        )
                                      }
                                      className="px-2 py-1 text-xs bg-red-500 text-white rounded"
                                    >
                                      Hold
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <JobCardModal
        show={showJobCard}
        mode={currentJobCard?.mode || "designed"}
        row={currentJobCardRow}
        queueIndex={jobCardIndex}
        queueTotal={jobCardTotal}
        onClose={closeJobCard}
        onSubmit={handleJobCardSubmit}
        submitting={jobCardSubmitting}
        employee={employee}
        user={user}
        empList={jobCardEmpList}
        filesByList={allEmployees?.filter(
          (item: any) =>
            item?.verified &&
            ["Designer", "Senior Designer", "Captain", "Assistant Captain", "Super Admin"].includes(
              item.role,
            ),
        )}
        passkey={passkey}
        setPasskey={setPasskey}
        customQC={customQC}
        setCustomQC={setCustomQC}
        actionNote={actionNote}
        setActionNote={setActionNote}
        error={error || ""}
      />
      <ConfirmModal
        show={showConfirm}
        onClose={() => {
          setShowConfirm(false);
          setPendingAction(null);
          setPasskey("");
          setActionNote("");
        }}
        onConfirm={handleConfirm}
        employee={employee}
        empList={filteredEmployees}
        user={user}
        passkey={passkey}
        setPasskey={setPasskey}
        customQC={customQC}
        setCustomQC={setCustomQC}
        actionNote={actionNote}
        setActionNote={setActionNote}
        error={error || ""}
        pendingAction={pendingAction}
        hasBulkQC2={hasBulkQC2}
        selectLabel={confirmSelectLabel}
      />
      {showEditModal && (
        <EditAllocationModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setPasskey("");
          }}
          onSave={handleEditSave}
          currentDesigner={newDesigner}
          currentCaptain={newCaptain}
          onDesignerChange={setNewDesigner}
          onCaptainChange={setNewCaptain}
          employee={employee}
          user={user}
          passkey={passkey}
          editNote={editNote}
          setEditNote={setEditNote}
          setPasskey={setPasskey}
        />
      )}

      <CaseNotesModal
        isOpen={open}
        onClose={() => setOpen(false)}
        allocationId={selectedAllocationId}
        caseId={selectedNotesCaseId}
      />
      <CaseOkQueueModal
        isOpen={showCaseOkQueue}
        onClose={() => setShowCaseOkQueue(false)}
      />
    </div>
  );
};

export default AllocateLive;